import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { backendGet, backendDelete } from "services/dataService.js";
import { useSnackbar } from "notistack";
import { useUser } from "../../../hooks/user.js";
import { getUserRowsPerPage } from "../../../utils/userSettings.js";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import Spinner from "components/Spinner.jsx";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import AssignExportModal from "./components/AssignExportModal.jsx";
import PaymentImportStatusBadge from "components/PaymentImportStatusBadge.jsx";
import PaymentImportPaymentsTable from "components/tables/PaymentImportPaymentsTable.jsx";

const PaymentImportShowPage = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const navigate = useNavigate();
  const { id } = useParams(); // ID des PaymentImports aus der URL
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentImport, setPaymentImport] = useState(null); // file_name speichern
  const [assignExportModalOpen, setAssignExportModalOpen] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const default_rows_per_page = getUserRowsPerPage(user, 100);

  const fetchPaymentImport = useCallback(async () => {
    setLoading(true);
    try {
      // const paymentsPromise = backendGet(
      //   `data/payment-imports/${id}/payments`,
      //   {
      //     label: "Payments",
      //     enqueueSnackbar,
      //     setResult: (res) => setPayments(res.data),
      //   }
      // );

      const paymentImportPromise = backendGet(`data/payment-imports/${id}`, {
        label: "PaymentImport",
        enqueueSnackbar,
        setResult: (res) => setPaymentImport(res.data),
      });

      await Promise.all([paymentImportPromise]);
    } catch (err) {
      setError("Fehler beim Laden der Daten.");
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    fetchPaymentImport();
    console.log("fetching data for payment import payments");
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", color: "secondary", path: "/home" },
      {
        name: "Buchungsverwaltung",
        color: "primary",
        path: "/admin/buchungen",
      },
      {
        name: "Zahlungsimporte",
        color: "primary",
        path: "/admin/buchungen/imports",
      },
      {
        name: `${paymentImport?.file_name}`,
        color: "primary",
        path: `/admin/buchungen/imports/${id}`,
      },
    ]);
  }, [setBreadcrumbs, paymentImport?.id]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const handleRowClick = (payment) => {
    setSelectedPayment(payment);
    setModalOpen(true);
  };

  const handleAssignSchadenClick = () => {
    setModalOpen(false);
    navigate(`/admin/buchungen/imports/${id}/payment-matching`);
  };

  if (loading) return <Spinner />;
  if (error) return <p>{error}</p>;

  return (
    <>
      <PageHeader
        title={`Importdetails: ${paymentImport?.file_name ?? ""}`}
        subtitle="Details und Zahlungen des Importvorgangs"
      />

      <PaymentImportStats paymentImport={paymentImport} payments={payments} />

      <PaymentImportPaymentsTable
        paymentImportId={id}
        defaultRowsPerPage={default_rows_per_page}
        onRowClick={handleRowClick}
      />

      {/* Modal für Zahlungsdetails */}
      <PaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        payment={selectedPayment}
        onActionComplete={() => {
          setModalOpen(false);
          fetchPaymentImport();
        }}
        onAssignToExport={() => {
          setModalOpen(false);
          setAssignExportModalOpen(true);
        }}
        onAssignSchaden={handleAssignSchadenClick}
      />

      {selectedPayment && (
        <AssignExportModal
          open={assignExportModalOpen}
          onClose={() => setAssignExportModalOpen(false)}
          payment={selectedPayment}
          onActionComplete={() => {
            setAssignExportModalOpen(false);
            fetchPaymentImport();
          }}
        />
      )}
    </>
  );
};

import { useBreadcrumb } from "../../../contexts/BreadcrumbContext.jsx";
import { PageHeader } from "../../../theme/components/index.js";
import { formatDatePeriod } from "services/formatService.js";

const PaymentImportStats = ({ paymentImport, payments }) => {
  const countAll = payments.length;
  const countAssigned = payments.filter(
    (p) => !!p.booking_id || !!p.booking_export_id
  ).length;
  const countFailed = payments.filter((p) => p.status === "failed").length;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("de-DE", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  return (
    <div style={{ display: "flex", gap: 32, margin: "24px 0" }}>
      <div>
        <strong>Status:</strong>
        <br />
        <PaymentImportStatusBadge status={paymentImport?.status} />
      </div>
      <div>
        <strong>Zeitraum:</strong>
        <br />
        {formatDatePeriod(paymentImport?.from_date, paymentImport?.to_date)}
      </div>
      <div>
        <strong>Gesamt:</strong>
        <br />
        {countAll}
      </div>
      <div>
        <strong>Zugewiesen:</strong>
        <br />
        {countAssigned}
      </div>
      <div>
        <strong>Fehlerhaft:</strong>
        <br />
        {countFailed}
      </div>
      <div>
        <strong>Importiert am:</strong>
        <br />
        {formatDate(paymentImport?.created_at)}
      </div>
    </div>
  );
};

const ActionRow = ({ button, description }) => (
  <Box
    sx={{
      display: "grid",
      gridColumn: "1 / -1",
      gridTemplateColumns: "subgrid",
      alignItems: "center",
      mb: 1.5,
      p: 1.5,
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      backgroundColor: "#fafafa",
    }}
  >
    <Box
      sx={{
        gridColumn: "1",
        justifySelf: "start",
        minWidth: "180px",
      }}
    >
      {React.cloneElement(button, {
        style: {
          width: "100%",
        },
      })}
    </Box>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ gridColumn: "2", justifySelf: "start", textAlign: "left" }}
    >
      {description}
    </Typography>
  </Box>
);

const PaymentModal = ({
  open,
  onClose,
  payment,
  onActionComplete,
  onAssignToExport,
  onAssignSchaden,
}) => {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchBooking = async (bookingId) => {
      setLoading(true);
      await backendGet(`data/bookings/${bookingId}`, {
        label: "Buchung",
        enqueueSnackbar,
        setResult: setBooking,
      });
      setLoading(false);
    };

    const bookingId =
      payment?.booking_id || payment?.error?.existing_payment?.booking_id;

    if (open && bookingId) {
      fetchBooking(bookingId);
    } else {
      setLoading(false);
      setBooking(null);
    }
  }, [open, payment, enqueueSnackbar]);

  const handleIgnore = async () => {
    await backendDelete(`data/payments/${payment.id}`, {
      label: "Zahlung ignorieren",
      setIsLoading: setIsSubmitting,
      enqueueSnackbar,
      onSuccess: onActionComplete,
    });
  };

  const handleGoToExport = () => {
    // This route needs to be created if it doesn't exist.
    navigate(`/admin/buchungen/exports/${payment.booking_export_id}`);
    onClose();
  };

  const handleGoToClaim = () => {
    if (booking?.schaden_id) {
      navigate(`/schaden/${booking.schaden_id}`);
      onClose();
    }
  };

  const handleAssignToExport = () => {
    onAssignToExport();
  };

  if (!payment) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Zahlungsdetails</DialogTitle>

      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          payment && (
            <div>
              <div>
                <strong>Sender:</strong> {payment.remitter}
              </div>
              <div>
                <strong>Betrag:</strong> {payment.amount}
              </div>
              <div>
                <strong>IBAN:</strong> {payment.iban}
              </div>
              <div>
                <strong>BIC:</strong> {payment.bic}
              </div>
              <div>
                <strong>Beschreibung:</strong> {payment.description}
              </div>

              {payment.error ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>{payment.error?.message}</AlertTitle>
                  {payment.error?.existing_payment && (
                    <p>Wurde bereits zu einem Schaden zugeordnet</p>
                  )}
                </Alert>
              ) : null}

              <Box
                sx={{
                  mt: 4,
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: "20px",
                }}
              >
                {payment?.status === "assigned" && payment.booking_id && (
                  <ActionRow
                    button={
                      <Button
                        onClick={handleGoToClaim}
                        color="primary"
                        variant="contained"
                      >
                        Schaden anzeigen
                      </Button>
                    }
                    description="Navigiert zur Detailansicht des zugeordneten Schadens."
                  />
                )}
                {payment?.status === "assigned" &&
                  payment.booking_export_id && (
                    <ActionRow
                      button={
                        <Button
                          onClick={handleGoToExport}
                          color="primary"
                          variant="contained"
                        >
                          Export anzeigen
                        </Button>
                      }
                      description="Zeigt den Buchungs-Export an, in dem diese Zahlung enthalten ist."
                    />
                  )}
                {payment?.status === "imported" && (
                  <>
                    <ActionRow
                      button={
                        <Button onClick={onAssignSchaden} color="primary">
                          Schaden zuordnen
                        </Button>
                      }
                      description="Startet den Prozess, um diese und weitere Zahlungen einem Schaden zuzuordnen."
                    />
                    <ActionRow
                      button={
                        <Button onClick={handleAssignToExport} color="primary">
                          Export zuordnen
                        </Button>
                      }
                      description="Weist diese Zahlung einem Zahlungs-Export zu."
                    />
                    <ActionRow
                      button={
                        <Button
                          onClick={handleIgnore}
                          color="error"
                          disabled={isSubmitting}
                        >
                          Ignorieren
                        </Button>
                      }
                      description="Entfernt diese Zahlung aus der Liste. Achtung: Die Aktion kann nicht rückgängig gemacht werden."
                    />
                  </>
                )}
              </Box>
            </div>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentImportShowPage;
